import React, { useEffect, useMemo } from "react";
import styles from "./Layout.module.scss";
import commonStyles from "../../pages/common.module.scss";
import { observer } from "mobx-react-lite";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSetupUrlStore } from "../../services/urlStore";
import { getI18nLanguageFromUserSetting } from "@whatslly/commons/LanguageChangeUtils";
import { NavItemType, SideBarNavMenu } from "@whatslly/ui-components/SideBarNavMenu";
import { NavMenuStore } from "./NavMenuStore";
import { TopBar } from "../../components/TopBar";
import classNames from "classnames";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { OrgMissingMessage } from "./OrgMissingMessage/OrgMissingMessage";
interface BaseLayoutProps {
  navigationOptions?: NavItemType[];
  children?: React.ReactNode;
}

export interface LayoutWithNavProps {
  navigationOptions: NavItemType[];
  parentUrl?: string;
  title?: React.ReactNode;
  children?: React.ReactNode;
  disablePadding?: boolean;
}

export const Layout: React.FC<BaseLayoutProps> = observer(({ navigationOptions, children }) => {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const organizationStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const navMenuStore = useDependencyInjector(NavMenuStore);
  const urlInitialized = useSetupUrlStore();

  useEffect(() => {
    const onLoadSelectedLang = getI18nLanguageFromUserSetting(userStore.user?.language);
    if (onLoadSelectedLang) {
      i18n.changeLanguage(onLoadSelectedLang);
    }
  }, [i18n, userStore.user?.language]);

  const navigationOptionsWithSearchParams = useMemo(
    () => navigationOptions?.map((option) => ({ ...option, to: `${option.to}${search}` })) ?? [],
    [navigationOptions, search]
  );

  const hasMatchingPage = useMemo(
    () =>
      Boolean(
        !pathname ||
          pathname === "/" ||
          navMenuStore.availablePaths.find((path) => {
            const url = new URL(path, location.origin);
            return url.pathname === pathname || pathname.startsWith(url.pathname);
          })
      ),
    [pathname, navMenuStore.availablePaths]
  );

  if (!userStore.initialized) {
    return null;
  }
  if (!userStore.user) {
    const redirectToWithSearch = search ? `${pathname}${search}` : pathname;
    const normalizeRedirectTo =
      !pathname.includes("login") && pathname !== "/" ? new URLSearchParams({ redirectTo: redirectToWithSearch }).toString() : undefined;
    const toPath = normalizeRedirectTo ? `/login?${normalizeRedirectTo}` : "/login";
    return <Navigate to={toPath} replace />;
  }

  if (!hasMatchingPage) {
    if (userStore.hasAccessToMultipleOrgs) {
      return <Navigate to={`/${search}`} replace />;
    }

    return <Navigate to={`/login?error=${decodeURIComponent(t("login.no_sufficient_permissions"))}`} replace />;
  }

  if (!urlInitialized) {
    return null;
  }

  const orgDoesNotExist = organizationStore.orgId.case({
    fulfilled: (value) => false,
    pending: () => false,
    rejected: (error) => error.message === "Failed to get org",
  });

  return (
    <div className={styles.layout}>
      <TopBar navigationOptionsWithSearchParams={navigationOptionsWithSearchParams} />
      <div className={styles.wrapper}>
        {children}
        {orgDoesNotExist && <OrgMissingMessage />}
      </div>
    </div>
  );
});

export const LayoutWithNav: React.FC<LayoutWithNavProps> = observer(({ navigationOptions, children, title, disablePadding }) => {
  const { search } = useLocation();
  const navMenuStore = useDependencyInjector(NavMenuStore);
  const [navMenuItemsWithSearchParams, navigationOptionsWithSearchParams] = useMemo(
    () => [
      navMenuStore.tree.items.map((option) => ({ ...option, to: `${option.to}${search}` })),
      navigationOptions.map((option) => ({ ...option, to: `${option.to}${search}` })),
    ],
    [navMenuStore, navigationOptions, search]
  );
  const hasSubMenu = useMemo(() => navigationOptions[0] !== navMenuStore.tree.items[0], [navigationOptions, navMenuStore.tree.items]);
  return (
    <Layout navigationOptions={navigationOptions}>
      {<SideBarNavMenu menuItems={navMenuItemsWithSearchParams} minimized={hasSubMenu} isLoading={navMenuStore.isMenuLoading} />}
      {hasSubMenu && (
        <SideBarNavMenu
          minimized={false}
          menuItems={navigationOptionsWithSearchParams}
          subMenu={true}
          title={title}
          isLoading={navMenuStore.isSubMenuLoading || navMenuStore.isMenuLoading}
        />
      )}
      <div data-testid="layout-content" className={disablePadding ? styles.contentNoPadding : classNames(styles.content, commonStyles.container)}>
        {children ?? <Outlet />}
      </div>
    </Layout>
  );
});
